import { setErrorsToForm } from "@/utils/formUtils";
import { cssVariables, isDefined, parseBirthDateFromPin } from "@/utils/utils";
import { Drawer, Form } from "antd";
import { useTranslation } from "react-i18next";
import { DrawerSizes } from "../../../../common/constants";
import { FieldConstraintViolation } from "../../../../common/types";
import { ClientFormType, ClientType } from "../../enums";
import { Client, CreateUpdateClient, LegalClient } from "../../types";
import {
  createClientAggregatedName,
  createClientAggregatedNaturalName,
  processClientFormValues,
  resolveClientIdentifier,
} from "../../utils";
import { ClientFormBody } from "../forms/ClientFormBody";
import { ClientTypeTag } from "../tags/ClientTypeTag";

interface Props<Type = ClientFormType> {
  open: boolean;
  client?: Partial<Client>;
  initialClientType?: ClientType;
  initialIdentifier?: string;
  requireIdCardNumber?: boolean;
  formType?: Type;
  disabled?: boolean;
  violationErrors?: Map<Type, FieldConstraintViolation[]>;
  onFormSubmit: (client: Client, clientFormType: Type) => void;
  onFormValuesChange?: (changedValues: any, values: any) => void;
  onlyIdCardIdentityTypeSupport?: boolean;
}

const ClientDrawerForm = <Type,>(props: Props<Type>) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateClient>();

  const handleFormSubmit = (clientData: CreateUpdateClient) => {
    if (isDefined(props.formType)) {
      let client = props.client
        ? ({ ...props.client, ...clientData } as Partial<Client>)
        : ({ ...clientData } as Partial<Client>);

      client = {
        ...client,
        identifier: resolveClientIdentifier(client),
        aggregatedName: createClientAggregatedName(client),
      };

      if (client.type === ClientType.SELF_EMPLOYED) {
        client = { ...client, aggregatedNaturalName: createClientAggregatedNaturalName(client) };
      }
      if (client.type === ClientType.LEGAL && props.client) {
        client = { ...client, representatives: (props.client as LegalClient).representatives || [] };
      }

      props.onFormSubmit(client as Client, props.formType);
    }
  };

  const handleDrawerClose = () => {
    handleFormSubmit(processClientFormValues(form.getFieldsValue()));
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (open && props.client) {
      const violations = isDefined(props.formType) ? props.violationErrors?.get(props.formType) : undefined;
      if (violations?.length) {
        setErrorsToForm(form, "client.attrs", violations);
      }
    }
  };

  const initialPinValue =
    !props.client &&
    (props.initialClientType === ClientType.NATURAL || (props.initialIdentifier && props.initialIdentifier?.length > 8))
      ? props.initialIdentifier
      : undefined;

  const initialCrnValue =
    !props.client &&
    (props.initialClientType === ClientType.SELF_EMPLOYED ||
      props.initialClientType === ClientType.LEGAL ||
      props.initialIdentifier?.length === 8)
      ? props.initialIdentifier
      : undefined;

  return (
    <Drawer
      title={
        <h3 style={{ marginBottom: 0, display: "flex", alignItems: "center" }}>
          {props.client ? (
            <>
              <span>{`${t("client.titles.update")}: ${props.client.aggregatedName ?? ""}`}</span>
              <ClientTypeTag style={{ marginLeft: cssVariables.smallSpace }} type={props.client.type} />
            </>
          ) : (
            t("client.titles.create")
          )}
        </h3>
      }
      size={DrawerSizes.LARGE}
      open={props.open}
      closable
      destroyOnHidden
      afterOpenChange={handleDrawerOpenChange}
      onClose={handleDrawerClose}
    >
      <Form
        form={form}
        layout="vertical"
        name="clientDrawerForm"
        disabled={props.disabled}
        initialValues={{
          personalIdentificationNumber: initialPinValue,
          birthDate: initialPinValue ? parseBirthDateFromPin(initialPinValue) : undefined,
          companyRegistrationNumber: initialCrnValue,
        }}
        onValuesChange={props.onFormValuesChange}
      >
        <ClientFormBody
          form={form}
          client={props.client}
          initialClientType={props.initialClientType}
          excludedClientTypes={
            props.initialClientType
              ? undefined
              : props.initialIdentifier?.length === 8
                ? [ClientType.NATURAL]
                : [ClientType.SELF_EMPLOYED, ClientType.LEGAL]
          }
          disablePinInput={!!initialPinValue || props.client?.type === ClientType.NATURAL}
          disableCrnInput={
            !!initialCrnValue ||
            props.client?.type === ClientType.SELF_EMPLOYED ||
            props.client?.type === ClientType.LEGAL
          }
          requireIdCardNumber={props.requireIdCardNumber}
          viewType="drawer"
          onFormSubmit={handleFormSubmit}
          onlyIdCardIdentityTypeSupport={props.onlyIdCardIdentityTypeSupport}
          isCreatingNewClient={!props.client?.id}
        />
      </Form>
    </Drawer>
  );
};

export default ClientDrawerForm;
